import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeading from '../PageHeading/PageHeading';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import mainApi from '../../../assets/api/MainApi';
import {
    ADVERTISING_FORM_LABELS,
    formatAdvertisingDate,
    formatAdvertisingDateOnly,
    formatAdvertisingMoney,
    getAdvertiserTypeTitle,
    getCampaignStatusTitle,
    getPaymentUrl,
    parseAdvertisingApiError,
} from './advertisingCampaignsHelpers';

const HIDDEN_FORM_KEYS = ['contact_phone', 'product_category'];

function hasValue(value) {
    if (value === null || value === undefined) return false;
    const preparedValue = String(value).trim();
    return Boolean(preparedValue) && preparedValue !== '-' && preparedValue !== 'Пусто';
}

function DetailField({ label, value, wide = false, accent = false }) {
    if (!hasValue(value)) return null;

    return (
        <div className={`advertising-campaigns-detail__field ${wide ? 'advertising-campaigns-detail__field_wide' : ''} ${accent ? 'advertising-campaigns-detail__field_accent' : ''}`}>
            <span>{label}</span>
            <p>{value}</p>
        </div>
    );
}

function AdvertisingCampaignDetail() {
    const { promotionId } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!promotionId) return undefined;

        let isCancelled = false;
        setIsLoading(true);
        setError('');

        mainApi.getAdvertisingCampaign({ promotion_id: promotionId })
            .then((res) => {
                if (!isCancelled) setCampaign(res);
            })
            .catch((err) => {
                console.log(err);
                if (!isCancelled) {
                    setCampaign(null);
                    setError(parseAdvertisingApiError(err));
                }
            })
            .finally(() => {
                if (!isCancelled) setIsLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [promotionId]);

    const formRows = useMemo(() => {
        if (!campaign?.form_data) return [];

        return Object.keys(ADVERTISING_FORM_LABELS).reduce((acc, key) => {
            if (HIDDEN_FORM_KEYS.includes(key)) return acc;

            const value = key === 'advertiser_type'
                ? getAdvertiserTypeTitle(campaign.form_data[key])
                : campaign.form_data[key];

            if (!hasValue(value)) return acc;

            acc.push({
                key,
                label: ADVERTISING_FORM_LABELS[key],
                value,
            });
            return acc;
        }, []);
    }, [campaign]);

    const summaryRows = useMemo(() => {
        if (!campaign) return [];

        return [
            {
                key: 'amount',
                label: 'Стоимость',
                value: formatAdvertisingMoney(campaign.amount, campaign.currency),
                accent: true,
            },
            {
                key: 'created_at',
                label: 'Заявка создана',
                value: formatAdvertisingDate(campaign.created_at),
            },
            {
                key: 'paid_at',
                label: 'Оплачено',
                value: formatAdvertisingDate(campaign.paid_at),
            },
            {
                key: 'completed_at',
                label: 'Завершено',
                value: formatAdvertisingDate(campaign.completed_at),
            },
        ].filter((row) => hasValue(row.value));
    }, [campaign]);

    const paymentUrl = getPaymentUrl(campaign);
    const canPay = campaign?.status === 'waiting_payment' && paymentUrl;
    const hasPeriod = hasValue(campaign?.started_at) || hasValue(campaign?.ends_at);
    const periodText = [
        campaign?.started_at ? formatAdvertisingDateOnly(campaign.started_at) : '',
        campaign?.ends_at ? formatAdvertisingDateOnly(campaign.ends_at) : '',
    ].filter(Boolean).join(' - ');

    return (
        <div className='advertising-campaigns-detail'>
            <PageHeading
                className='advertising-campaigns-detail__heading'
                title='Карточка продвижения'
                goBack={{
                    onClick: () => navigate(-1),
                }}
            />

            {isLoading ? (
                <div className='advertising-campaigns__preloader'>
                    <MiniPreloader />
                </div>
            ) : error ? (
                <p className='advertising-campaigns__error'>{error}</p>
            ) : campaign ? (
                <div className='advertising-campaigns-detail__content'>
                    <section className='advertising-campaigns-detail__hero'>
                        <div className='advertising-campaigns-detail__hero-head'>
                            <div className='advertising-campaigns-detail__hero-title-box'>
                                <p className='advertising-campaigns-detail__store'>{campaign.store_name || 'Магазин'}</p>
                                <p className='advertising-campaigns-detail__tariff'>{campaign.tariff_title || campaign.tariff_code || 'Тариф'}</p>
                            </div>
                            <span className={`advertising-campaigns__status advertising-campaigns__status_${campaign.status || 'default'}`}>
                                {getCampaignStatusTitle(campaign)}
                            </span>
                        </div>

                        {summaryRows.length > 0 ? (
                            <div className='advertising-campaigns-detail__summary-grid'>
                                {summaryRows.map((row) => (
                                    <DetailField
                                        label={row.label}
                                        value={row.value}
                                        accent={row.accent}
                                        key={`advertising-detail-summary-${row.key}`}
                                    />
                                ))}
                            </div>
                        ) : null}

                        {hasPeriod ? (
                            <div className='advertising-campaigns-detail__period'>
                                <p>Период продвижения</p>
                                <span>{periodText}</span>
                            </div>
                        ) : null}

                        {canPay ? (
                            <a className='advertising-campaigns__main-btn advertising-campaigns-detail__payment' href={paymentUrl} target='_blank' rel='noreferrer'>
                                Перейти к оплате
                            </a>
                        ) : null}
                    </section>

                    {formRows.length > 0 ? (
                        <section className='advertising-campaigns-detail__section'>
                            <h4 className='advertising-campaigns-detail__section-title'>Заявка</h4>
                            <div className='advertising-campaigns-detail__fields'>
                                {formRows.map((row) => (
                                    <DetailField
                                        label={row.label}
                                        value={row.value}
                                        wide={row.key === 'comment' || row.key === 'store_url'}
                                        key={`advertising-detail-form-${row.key}`}
                                    />
                                ))}
                            </div>
                        </section>
                    ) : null}

                    {campaign.report_text ? (
                        <section className='advertising-campaigns-detail__section'>
                            <div className='advertising-campaigns-detail__report-heading'>
                                <h4 className='advertising-campaigns-detail__section-title'>Отчет</h4>
                                {campaign.report_sent_at ? (
                                    <span>{formatAdvertisingDate(campaign.report_sent_at)}</span>
                                ) : null}
                            </div>
                            <p className='advertising-campaigns-detail__report'>{campaign.report_text}</p>
                        </section>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

export default AdvertisingCampaignDetail;
